import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { clubs } from '../../lib/api';
import {
  Award,
  Building2,
  Car,
  ChevronRight,
  Dumbbell,
  Filter,
  Lock,
  Map,
  MapPin,
  Search,
  ShieldCheck,
  ShowerHead,
  Sparkles,
  Star,
  Target,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SURFACE_OPTIONS = ['clay', 'hard', 'grass', 'carpet', 'synthetic'];

const SURFACE_META = {
  hard: { labelKey: 'club.surfaces.hard', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  clay: { labelKey: 'club.surfaces.clay', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  grass: { labelKey: 'club.surfaces.grass', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  carpet: { labelKey: 'club.surfaces.carpet', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  synthetic: { labelKey: 'club.surfaces.synthetic', className: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  indoor: { labelKey: 'club.surfaces.indoor', className: 'bg-zinc-900 text-white border-zinc-800' },
  other: { labelKey: 'club.surfaces.other', className: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
};

const CONDITION_META = {
  parking: { labelKey: 'club.amenitiesList.parking', icon: Car },
  shower: { labelKey: 'club.amenitiesList.shower', icon: ShowerHead },
  lockers: { labelKey: 'club.amenitiesList.lockers', icon: Lock },
  gym: { labelKey: 'club.amenitiesList.gym', icon: Dumbbell },
};

const getSurfaceKey = (surface) => {
  const key = String(surface || '').toLowerCase();
  return SURFACE_META[key] ? key : 'other';
};

const formatLocation = (club, emptyLabel) =>
  [club.address, club.city, club.country].filter(Boolean).join(', ') || emptyLabel;

const getClubCourts = (club) => (Array.isArray(club?.courts) ? club.courts : []);
const getClubTrainers = (club) => (Array.isArray(club?.trainers) ? club.trainers : []);

const getSurfaces = (club) => {
  const surfaceSet = new Set();
  getClubCourts(club).forEach((court) => {
    if (court?.type) surfaceSet.add(getSurfaceKey(court.type));
    if (court?.indoor) surfaceSet.add('indoor');
  });
  return [...surfaceSet];
};

const getMinPrice = (club) => {
  const prices = getClubCourts(club)
    .map((court) => Number(court?.price_hour))
    .filter((price) => Number.isFinite(price) && price > 0);
  return prices.length ? Math.min(...prices) : null;
};

const getAmenityList = (club) =>
  Object.entries(CONDITION_META)
    .filter(([key]) => Boolean(club?.conditions?.[key]))
    .map(([key, meta]) => ({ key, ...meta }));

const getMemberCount = (club) => (Array.isArray(club?.members) ? club.members.length : 0);

const SurfaceBadge = ({ surface, t }) => {
  const meta = SURFACE_META[getSurfaceKey(surface)] || SURFACE_META.other;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}>
      {t(meta.labelKey)}
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, tone = 'lime' }) => {
  const toneClass = {
    lime: 'bg-lime-100 text-lime-700',
    blue: 'bg-blue-100 text-blue-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    zinc: 'bg-zinc-100 text-zinc-700',
  }[tone];

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneClass}`}>
          <Icon size={20} />
        </span>
        <div>
          <p className="text-2xl font-semibold tracking-tight text-zinc-950">{value}</p>
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

const ClubsSkeleton = () => (
  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
    <div className="grid gap-4 md:grid-cols-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-64 animate-pulse rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="h-12 w-12 rounded-2xl bg-zinc-100" />
          <div className="mt-5 h-5 w-3/4 rounded bg-zinc-100" />
          <div className="mt-3 h-3 w-full rounded bg-zinc-100" />
          <div className="mt-2 h-3 w-2/3 rounded bg-zinc-100" />
          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className="h-16 rounded-2xl bg-zinc-100" />
            <div className="h-16 rounded-2xl bg-zinc-100" />
            <div className="h-16 rounded-2xl bg-zinc-100" />
          </div>
        </div>
      ))}
    </div>
    <div className="hidden h-[560px] animate-pulse rounded-3xl border border-zinc-200 bg-white shadow-sm lg:block" />
  </div>
);

const EmptyState = ({ hasFilters, onClear, t }) => (
  <div className="rounded-3xl border border-dashed border-zinc-300 bg-white px-6 py-14 text-center shadow-sm">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
      <Building2 size={26} />
    </div>
    <h3 className="mt-5 text-lg font-semibold text-zinc-950">{t('club.emptyTitle')}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-600">
      {t(hasFilters ? 'club.emptyFilteredBody' : 'club.emptyBody')}
    </p>
    {hasFilters && (
      <button
        type="button"
        onClick={onClear}
        className="mt-5 inline-flex min-h-11 items-center justify-center rounded-full border border-zinc-300 px-4 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
      >
        {t('club.clearFilters')}
      </button>
    )}
  </div>
);

const ErrorState = ({ onRetry, t }) => (
  <div className="rounded-3xl border border-rose-200 bg-rose-50 px-6 py-12 text-center">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-rose-600">
      <Filter size={26} />
    </div>
    <h3 className="mt-5 text-lg font-semibold text-zinc-950">{t('club.loadErrorTitle')}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-rose-700">
      {t('club.loadErrorBody')}
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-5 inline-flex min-h-11 items-center justify-center rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
    >
      {t('club.retry')}
    </button>
  </div>
);

const ClubCard = ({ club, selected, onSelect, t }) => {
  const surfaces = getSurfaces(club);
  const minPrice = getMinPrice(club);
  const courts = getClubCourts(club);
  const trainers = getClubTrainers(club);
  const amenities = getAmenityList(club);
  const isVerified = Boolean(club?.verified || club?.is_verified);

  return (
    <button
      type="button"
      onClick={() => onSelect(club)}
      className={`group flex min-h-[260px] w-full flex-col rounded-3xl border bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-lg ${
        selected ? 'border-lime-400 ring-2 ring-lime-200' : 'border-zinc-200'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {club.logo_url ? (
            <img src={club.logo_url} alt={club.name} className="h-14 w-14 rounded-2xl object-cover" />
          ) : (
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
              <Building2 size={25} />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-lg font-semibold tracking-tight text-zinc-950">{club.name}</h3>
              {isVerified && (
                <span className="inline-flex items-center gap-1 rounded-full bg-lime-100 px-2 py-0.5 text-xs font-semibold text-lime-700">
                  <ShieldCheck size={12} />
                  {t('club.verified')}
                </span>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-zinc-500">
              <MapPin size={13} />
              <span className="truncate">{[club.city, club.country].filter(Boolean).join(', ') || t('club.locationNotSet')}</span>
            </p>
          </div>
        </div>

        {club.rating ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
            <Star size={13} fill="currentColor" />
            {Number(club.rating).toFixed(1)}
          </span>
        ) : null}
      </div>

      <p className="mt-4 line-clamp-2 text-sm leading-6 text-zinc-600">
        {club.description || t('club.noDescription')}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {surfaces.length > 0 ? surfaces.slice(0, 4).map((surface) => <SurfaceBadge key={surface} surface={surface} t={t} />) : (
          <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs font-semibold text-zinc-500">
            {t('club.surfaceNotListed')}
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <div className="rounded-2xl bg-zinc-50 p-3">
          <p className="text-lg font-semibold text-zinc-950">{courts.length}</p>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-500">{t('club.courts')}</p>
        </div>
        <div className="rounded-2xl bg-zinc-50 p-3">
          <p className="text-lg font-semibold text-zinc-950">{trainers.length}</p>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-500">{t('club.coaches')}</p>
        </div>
        <div className="rounded-2xl bg-zinc-50 p-3">
          <p className="text-lg font-semibold text-zinc-950">{minPrice ? `${minPrice}` : '-'}</p>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-500">RUB/h</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-zinc-100 pt-4">
        <div className="flex min-w-0 flex-wrap gap-2">
          {amenities.length > 0 ? amenities.slice(0, 3).map(({ key, labelKey, icon: Icon }) => (
            <span key={key} className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              <Icon size={12} />
              {t(labelKey)}
            </span>
          )) : (
            <span className="text-xs font-medium text-zinc-400">{t('club.amenitiesNotListed')}</span>
          )}
        </div>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lime-700 transition group-hover:bg-lime-300 group-hover:text-zinc-950">
          <ChevronRight size={18} />
        </span>
      </div>
    </button>
  );
};

const ClubDetailPanel = ({ club, t }) => {
  const courts = getClubCourts(club);
  const trainers = getClubTrainers(club);
  const amenities = getAmenityList(club);
  const surfaces = getSurfaces(club);
  const memberCount = getMemberCount(club);

  if (!club) {
    return (
      <aside className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-500">
            <Target size={26} />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-zinc-950">{t('club.selectClub')}</h3>
          <p className="mt-2 text-sm leading-6 text-zinc-600">
            {t('club.selectClubBody')}
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sticky top-6 space-y-4 self-start rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="rounded-2xl bg-zinc-950 p-5 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-300">{t('club.clubProfile')}</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">{club.name}</h2>
            <p className="mt-2 flex items-start gap-2 text-sm leading-6 text-zinc-300">
              <MapPin size={16} className="mt-0.5 shrink-0 text-lime-300" />
              {formatLocation(club, t('club.locationNotSpecified'))}
            </p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-lime-300 text-zinc-950">
            <Building2 size={24} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-2xl border border-zinc-200 p-3">
          <p className="text-xl font-semibold text-zinc-950">{courts.length}</p>
          <p className="text-xs font-medium text-zinc-500">{t('club.courts')}</p>
        </div>
        <div className="rounded-2xl border border-zinc-200 p-3">
          <p className="text-xl font-semibold text-zinc-950">{trainers.length}</p>
          <p className="text-xs font-medium text-zinc-500">{t('club.coaches')}</p>
        </div>
        <div className="rounded-2xl border border-zinc-200 p-3">
          <p className="text-xl font-semibold text-zinc-950">{memberCount}</p>
          <p className="text-xs font-medium text-zinc-500">{t('club.members')}</p>
        </div>
      </div>

      {club.description && (
        <div className="rounded-2xl border border-zinc-200 p-4">
          <h3 className="text-sm font-semibold text-zinc-950">{t('club.about')}</h3>
          <p className="mt-2 text-sm leading-6 text-zinc-600">{club.description}</p>
        </div>
      )}

      <div className="rounded-2xl border border-zinc-200 p-4">
        <h3 className="text-sm font-semibold text-zinc-950">{t('club.courtSummary')}</h3>
        {courts.length > 0 ? (
          <div className="mt-3 space-y-2">
            {courts.map((court, index) => (
              <div key={`${court.type}-${court.indoor}-${index}`} className="flex items-center justify-between rounded-xl bg-zinc-50 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <SurfaceBadge surface={court.type} t={t} />
                  {court.indoor && <SurfaceBadge surface="indoor" t={t} />}
                </div>
                <span className="text-sm font-semibold text-zinc-700">
                  {court.price_hour ? `${court.price_hour} RUB/h` : t('club.priceNotListed')}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-zinc-500">{t('club.courtInfoUnavailable')}</p>
        )}
      </div>

      <div className="rounded-2xl border border-zinc-200 p-4">
        <h3 className="text-sm font-semibold text-zinc-950">{t('club.coaches')}</h3>
        {trainers.length > 0 ? (
          <div className="mt-3 space-y-2">
            {trainers.map((trainer, index) => (
              <div key={`${trainer.name}-${index}`} className="rounded-xl bg-zinc-50 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-zinc-900">{trainer.name}</p>
                  {trainer.price_hour && <p className="text-sm font-semibold text-lime-700">{trainer.price_hour} RUB/h</p>}
                </div>
                <p className="mt-0.5 text-xs font-medium capitalize text-zinc-500">{trainer.level || t('club.levelNotListed')}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-zinc-500">{t('club.coachInfoUnavailable')}</p>
        )}
      </div>

      <div className="rounded-2xl border border-zinc-200 p-4">
        <h3 className="text-sm font-semibold text-zinc-950">{t('club.amenities')}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {amenities.length > 0 ? amenities.map(({ key, labelKey, icon: Icon }) => (
            <span key={key} className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-zinc-700">
              <Icon size={13} />
              {t(labelKey)}
            </span>
          )) : (
            <span className="text-sm text-zinc-500">{t('club.amenitiesUnavailable')}</span>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 p-4">
        <h3 className="text-sm font-semibold text-zinc-950">{t('club.surfacesLabel')}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {surfaces.length > 0 ? surfaces.map((surface) => <SurfaceBadge key={surface} surface={surface} t={t} />) : (
            <span className="text-sm text-zinc-500">{t('club.noSurfaceData')}</span>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50">
        <div className="flex h-36 items-center justify-center bg-[linear-gradient(135deg,#f4f4f5_25%,transparent_25%),linear-gradient(225deg,#f4f4f5_25%,transparent_25%),linear-gradient(45deg,#f4f4f5_25%,transparent_25%),linear-gradient(315deg,#f4f4f5_25%,#ffffff_25%)] bg-[length:24px_24px] bg-[position:12px_0,12px_0,0_0,0_0]">
          <div className="rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-600">
            <Map size={16} className="mr-2 inline" />
            {t('club.mapUnavailable')}
          </div>
        </div>
      </div>
    </aside>
  );
};

const ClubsPage = () => {
  const { t } = useTranslation();
  const [clubsList, setClubsList] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterSurface, setFilterSurface] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');

  const hasFilters = Boolean(searchQ || filterCity || filterSurface || filterMaxPrice);

  const selectedClub = useMemo(
    () => clubsList.find((club) => club.id === selectedClubId) || clubsList[0] || null,
    [clubsList, selectedClubId]
  );

  const stats = useMemo(() => {
    const totalCourts = clubsList.reduce((sum, club) => sum + getClubCourts(club).length, 0);
    const totalCoaches = clubsList.reduce((sum, club) => sum + getClubTrainers(club).length, 0);
    const amenityReady = clubsList.filter((club) => getAmenityList(club).length > 0).length;
    return { totalClubs: clubsList.length, totalCourts, totalCoaches, amenityReady };
  }, [clubsList]);

  const loadClubs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filterCity) params.city = filterCity;
      if (filterSurface) params.surface = filterSurface;
      if (filterMaxPrice) params.max_price = parseFloat(filterMaxPrice);

      const response = searchQ
        ? await clubs.search({ q: searchQ, ...params })
        : await clubs.getAll(params);

      setClubsList(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      console.error('Failed to load clubs:', requestError);
      setError(requestError);
      setClubsList([]);
    } finally {
      setLoading(false);
    }
  }, [searchQ, filterCity, filterSurface, filterMaxPrice]);

  useEffect(() => {
    const timer = setTimeout(loadClubs, 300);
    return () => clearTimeout(timer);
  }, [loadClubs]);

  useEffect(() => {
    if (!clubsList.length) {
      setSelectedClubId(null);
      return;
    }
    if (!clubsList.some((club) => club.id === selectedClubId)) {
      setSelectedClubId(clubsList[0].id);
    }
  }, [clubsList, selectedClubId]);

  const clearFilters = () => {
    setSearchQ('');
    setFilterCity('');
    setFilterSurface('');
    setFilterMaxPrice('');
  };

  return (
    <div className="min-h-screen bg-[#f7f8f4] px-4 py-5 pb-24 lg:px-8 lg:py-8 lg:pb-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-[2rem] bg-zinc-950 text-white shadow-xl">
          <div className="relative p-6 sm:p-8 lg:p-10">
            <div className="pointer-events-none absolute inset-0 opacity-30">
              <div className="absolute -right-20 top-0 h-72 w-72 rounded-full border border-lime-300/30" />
              <div className="absolute bottom-6 left-1/2 h-40 w-[60%] -translate-x-1/2 rounded-[50%] border border-lime-300/20" />
              <div className="absolute left-8 top-8 h-px w-56 bg-lime-300/40" />
            </div>

            <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-lime-200">
                  <Sparkles size={14} />
                  {t('club.heroKicker')}
                </div>
                <h1 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl">
                  {t('club.title')}
                </h1>
                <p className="mt-4 max-w-2xl text-base leading-7 text-zinc-300">
                  {t('club.subtitle')}
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-lime-300 text-zinc-950">
                    <ShieldCheck size={22} />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-white">{t('club.athleteAccess')}</h2>
                    <p className="mt-1 text-sm leading-6 text-zinc-300">
                      {t('club.athleteAccessBody')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard icon={Building2} label={t('club.title')} value={stats.totalClubs} />
          <MetricCard icon={Target} label={t('club.courts')} value={stats.totalCourts} tone="blue" />
          <MetricCard icon={Award} label={t('club.coaches')} value={stats.totalCoaches} tone="emerald" />
          <MetricCard icon={Car} label={t('club.withAmenities')} value={stats.amenityReady} tone="zinc" />
        </section>

        <section className="rounded-3xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 lg:grid-cols-[minmax(240px,1fr)_180px_180px_160px_auto]">
            <label className="relative block">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                placeholder={t('club.searchPlaceholder')}
                value={searchQ}
                onChange={(event) => setSearchQ(event.target.value)}
                className="min-h-12 w-full rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-sm font-medium text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-100"
              />
            </label>

            <input
              type="text"
              placeholder={t('club.city')}
              value={filterCity}
              onChange={(event) => setFilterCity(event.target.value)}
              className="min-h-12 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm font-medium text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-100"
            />

            <select
              value={filterSurface}
              onChange={(event) => setFilterSurface(event.target.value)}
              className="min-h-12 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm font-medium text-zinc-900 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-100"
            >
              <option value="">{t('club.allSurfaces')}</option>
              {SURFACE_OPTIONS.map((surface) => (
                <option key={surface} value={surface}>{t(SURFACE_META[surface]?.labelKey || 'club.surfaces.other')}</option>
              ))}
            </select>

            <input
              type="number"
              placeholder={t('club.maxPrice')}
              value={filterMaxPrice}
              onChange={(event) => setFilterMaxPrice(event.target.value)}
              className="min-h-12 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm font-medium text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-100"
              min="0"
            />

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-zinc-200 px-4 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
              >
                <X size={16} />
                {t('club.clearFilters')}
              </button>
            )}
          </div>
        </section>

        {loading ? (
          <ClubsSkeleton />
        ) : error ? (
          <ErrorState onRetry={loadClubs} t={t} />
        ) : clubsList.length === 0 ? (
          <EmptyState hasFilters={hasFilters} onClear={clearFilters} t={t} />
        ) : (
          <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_390px]">
            <div className="grid gap-4 md:grid-cols-2">
              {clubsList.map((club) => (
                <ClubCard
                  key={club.id}
                  club={club}
                  selected={selectedClub?.id === club.id}
                  onSelect={(nextClub) => setSelectedClubId(nextClub.id)}
                  t={t}
                />
              ))}
            </div>
            <ClubDetailPanel club={selectedClub} t={t} />
          </section>
        )}

        <section className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-zinc-950">{t('club.informationOnly')}</h2>
              <p className="mt-1 text-sm leading-6 text-zinc-600">
                {t('club.informationOnlyBody')}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-zinc-100 px-4 py-2 text-sm font-semibold text-zinc-700">
              <Users size={16} />
              {t('club.discoveryMode')}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClubsPage;
